<script lang="ts" setup>
import {
  tpgIconAddReaction,
  tpgIconAttachFile,
  tpgIconSend,
} from '@tpg/grammar-web-icons';
import { storeToRefs } from 'pinia';
import type { Range } from 'quill';
import type { Context } from 'quill/modules/keyboard';
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
  toRefs,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

import { GrammarQuaternaryButton } from '@nora-monorepo/grammar-ui';
import type { MessageAttachmentDTO } from '@nora-monorepo/nora-api-client';
import {
  bgSecondary,
  borderSeparatorNoTransparency,
  NoraEmojiPicker,
  textSecondary,
  useConfigStore,
  useConfirmModalStore,
} from '@nora-monorepo/ui-shared';

import type { FileUpload } from '#nora-chat-ui/components/open-conversations/messages/file';
import MessageUploads from '#nora-chat-ui/components/open-conversations/messages/message-attachments/MessageUploads.vue';
import MessageEditInput from '#nora-chat-ui/components/open-conversations/messages/message-edit/MessageEditInput.vue';
import MessageQuoteInput from '#nora-chat-ui/components/open-conversations/messages/message-quote/MessageQuoteInput.vue';
import type { ConversationState } from '#nora-chat-ui/states';
import { useAttachmentStore } from '#nora-chat-ui/stores/attachment-store';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';
import { useChatUserStore } from '#nora-chat-ui/stores/user-store';
import { loadMessageForEditing, serializeMessageEditor } from '#nora-chat-ui/utils/message-markdown';
import { chatCan } from '#nora-chat-ui/utils/permission';
import { NoraQuill } from '#nora-chat-ui/utils/quill';

interface MessageInputProps {
  conversationContainer: HTMLElement | null;
  conversationState: ConversationState;
  onAttachmentAdd: (file: File) => Promise<void>;
}

const props = defineProps<MessageInputProps>();
const { conversationState } = toRefs(props);
const { t } = useI18n();
const attachmentStore = useAttachmentStore();
const configStore = useConfigStore();
const conversationMessageStore = useConversationMessageStore();
const confirmModalStore = useConfirmModalStore();

const { uploads } = storeToRefs(attachmentStore);
const { quoteMessage, editingMessage } = storeToRefs(conversationMessageStore);

const input = ref<string>('');
const emojiPickerToggle = ref<HTMLElement>();
const fileInput = ref();
const editor = ref();
const filteredUploads = ref<FileUpload[]>([]);
const canSendMessage = ref<boolean>();
const emojiPickerOpen = ref();
const currentEditingIndex = ref<number | null>(null);
const previousMessageContent = ref<string | null>(null);
const isSendingMessage = ref(false);

let quill: NoraQuill | null = null;
let rendered = false;
let editingPlainMarkdown = false;
let originalMarkdown: string | undefined;

const isEditingThisConversation = computed<boolean>(() => {
  return conversationMessageStore.isEditingConversation(
    props.conversationState.conversationListResult.id,
  );
});

const quoteMessageInThisConversation = computed(
  () =>
    quoteMessage.value
    && quoteMessage.value.message.conversationId
    === props.conversationState.conversationListResult.id,
);

watch(
  () => props.conversationContainer,
  (newValue) => {
    if (newValue) {
      renderEditor();
    }
  },
);

watch(quoteMessage, (newValue) => {
  if (quoteMessageInThisConversation.value && newValue && quill) {
    quill.focus();
  }
});

onMounted(() => {
  if (props.conversationContainer) {
    renderEditor();
  }

  /*
  We add this to support removing pending uploads on refresh.

  This is a best-effort solution since we can't guarantee that the request will complete before the page unloads, but it should work in most cases and is better than leaving orphaned uploads in the store.
  In the worst case, the user will have files in the file browser that he did not expect. In that case, he can delete them. */
  window.addEventListener('beforeunload', removePendingUploads);
});

onUnmounted(async () => {
  window.removeEventListener('beforeunload', removePendingUploads);
  await removePendingUploads();
});

async function removePendingUploads() {
  await Promise.allSettled(filteredUploads.value.map(upload => attachmentStore.removeUploadById(upload.id)));
}

function renderEditor() {
  if (rendered) {
    return;
  }

  quill = new NoraQuill(editor.value, {
    bounds: editor.value,
    modules: {
      keyboard: {
        bindings: [
          {
            key: 'Enter',
            shiftKey: true,
            handler: (range: Range, context: Context) => {
              quill?.keyboard.handleEnter(range, context);
            },
          },
          {
            key: 'Enter',
            shiftKey: false,
            handler: () => {
              sendMessage();
            },
          },
          {
            key: 'ArrowUp',
            handler: () => {
              handleArrowUp();
            },
          },
          {
            key: 'ArrowDown',
            handler: () => {
              handleArrowDown();
            },
          },
          {
            key: 'Escape',
            handler: () => {
              clearInputs();
            },
          },
        ],
      },
      toolbar: [
        'bold',
        'italic',
        'strike',
        { list: 'ordered' },
        { list: 'bullet' },
        'link',
        'clean',
      ],
    },
    theme: 'bubble',
    formats: ['header', 'bold', 'italic', 'strike', 'list', 'link'],
  });

  quill.on('text-change', () => {
    if (!quill) {
      return;
    }

    const html = quill.getSemanticHTML();
    input.value = serializeMessageEditor(html, quill.getText(), editingPlainMarkdown, originalMarkdown);
  });

  quill.on('file-paste', async (file: File) => {
    if (isEditingThisConversation.value) {
      return;
    }

    await props.onAttachmentAdd(file);
  });

  quill.root.setAttribute('data-name', 'message-input');
  rendered = true;
}

function getMyMessages() {
  const messages = props.conversationState.messages;
  const userStore = useChatUserStore();
  const myMessages = messages
    .filter(
      messageState =>
        messageState.message.sentBy.id === userStore.getUserId
        && messageState.message.message
        && !messageState.message.deletedAt,
    )
    .reverse();
  return myMessages;
}

function updateEditingIndex(editingIndexChange: number) {
  const canChangeEditingIndex
    = input.value.trim() === '' || input.value === previousMessageContent.value;
  if (!canChangeEditingIndex) {
    return;
  }

  const myMessages = getMyMessages();
  if (myMessages.length < 1) {
    return;
  }

  const isEditingUpwards = editingIndexChange > 0;
  if (isEditingUpwards) {
    if (currentEditingIndex.value === null) {
      // Start editing the first message if we don't have a current index
      currentEditingIndex.value = 0;
    }
    else {
      currentEditingIndex.value = Math.min(
        myMessages.length - 1,
        currentEditingIndex.value + editingIndexChange,
      );
    }
  }
  else {
    if (currentEditingIndex.value === null || currentEditingIndex.value === 0) {
      // If we're moving down from the first message, clear the message input field
      clearInputs();
      return;
    }
    currentEditingIndex.value = Math.max(
      0,
      currentEditingIndex.value + editingIndexChange,
    );
  }

  const messageToEdit = myMessages[currentEditingIndex.value]?.message;
  if (messageToEdit && messageToEdit?.message) {
    conversationState.value.highlightedMessage = messageToEdit;
    editingMessage.value = { oldMessage: { message: messageToEdit } };
    previousMessageContent.value = messageToEdit.message;

    if (quill) {
      editingPlainMarkdown = loadMessageForEditing(quill, messageToEdit.message);
      originalMarkdown = messageToEdit.message;
      input.value = messageToEdit.message;
    }
  }
}

function handleArrowUp() {
  updateEditingIndex(1);
}

function handleArrowDown() {
  updateEditingIndex(-1);
}

watch(
  uploads,
  (newValue) => {
    filteredUploads.value = newValue.filter(
      upload =>
        upload.conversationId
        === props.conversationState.conversationListResult.id,
    ) as FileUpload[];
  },
  {
    deep: true,
  },
);

watch([input, filteredUploads], (newValue) => {
  canSendMessage.value = canSend(newValue[1], newValue[0]);
});

async function handleFileUpload(event: Event) {
  const eventTarget = event.target;
  if (!(eventTarget instanceof HTMLInputElement)) {
    return;
  }

  if (isEditingThisConversation.value) {
    eventTarget.value = '';
    return;
  }

  const selectedFiles = eventTarget.files;

  if (selectedFiles) {
    await Promise.all(
      Array.from(selectedFiles).map(async (file) => {
        await props.onAttachmentAdd(file);
      }),
    );
  }

  eventTarget.value = '';
}

function focusEndOfInput() {
  const textLength = quill?.getLength();
  if (textLength) {
    quill?.setSelection(textLength, 0);
  }
}

watch(editingMessage, async (newValue, oldValue) => {
  if (!isEditingThisConversation.value) {
    if (
      oldValue?.oldMessage.message.conversationId
      === props.conversationState.conversationListResult.id
    ) {
      clearInputs();
    }

    return;
  }

  attachmentStore.clearUploadsForConversation(
    props.conversationState.conversationListResult.id,
  );

  const messageToEdit = newValue?.oldMessage.message.message ?? '';

  if (quill) {
    editingPlainMarkdown = loadMessageForEditing(quill, messageToEdit);
    originalMarkdown = messageToEdit;
    input.value = messageToEdit;
  }

  await nextTick();
  focusEndOfInput();
});

function showFileUpload() {
  if (isEditingThisConversation.value) {
    return;
  }

  if (fileInput.value) {
    fileInput.value.click();
  }
}

async function sendTemplateMessage(message: string) {
  if (input.value !== '') {
    return;
  }

  await conversationMessageStore.createMessage(
    props.conversationState.conversationListResult.id,
    message,
  );
}

function uploadToAttachment(uploads: FileUpload[]): MessageAttachmentDTO[] {
  return uploads.map((upload) => {
    if (!upload.fileId) {
      throw new Error(
        'Tried to convert an upload that does not have a file ID.',
      );
    }

    return {
      id: upload.id,
      fileId: upload.fileId,
      fileName: upload.file.name,
      mimeType: upload.file.type,
      size: `${upload.file.size}`,
      lastModified: 0,
      height: upload.height,
      width: upload.width,
      urls: upload.urls,
    };
  });
}

const canUploadAttachments = chatCan(
  'create:chat.conversation.message.attachment',
  props.conversationState.conversationListResult.id,
);

function canSend(uploads: FileUpload[], inputString: string): boolean {
  if (isSendingMessage.value) {
    return false;
  }
  const isInputFilled = inputString.trim().length > 0;
  const hasUploads = uploads.length > 0;

  if (hasUploads) {
    const allUploadsSucceeded = uploads.every(
      upload => upload.status === 'success',
    );

    if (!allUploadsSucceeded) {
      return false;
    }
  }

  return isInputFilled || hasUploads;
}

function onDeleteEditedMessage() {
  if (editingMessage.value?.oldMessage) {
    conversationMessageStore.deleteMessage(editingMessage.value.oldMessage);
    clearInputs();
  }
}

async function sendMessage() {
  const isEditing = isEditingThisConversation.value;
  if (!canSend(filteredUploads.value, input.value) && !isEditing) {
    return;
  }

  isSendingMessage.value = true;
  const inputToSend = `${input.value}`;

  if (!isEditing) {
    clearInputs();
  }

  if (isEditing) {
    // We offer the user to delete the edited message if the input is empty
    if (inputToSend.trim() === '') {
      const oldMessage = editingMessage.value?.oldMessage.message.message ?? '';
      const hasAttachments
        = editingMessage.value?.oldMessage.message.attachments?.length;
      const deleteMessage = hasAttachments
        ? t('message.delete-message-prompt-with-message-and-attachment', [
            oldMessage,
          ])
        : t('message.delete-message-prompt-with-message', [oldMessage]);

      confirmModalStore.showConfirmModal({
        headerText: t('messageActions.delete'),
        bodyText: deleteMessage,
        onConfirm: onDeleteEditedMessage,
        isDelete: true,
      });
      isSendingMessage.value = false;
      // Return earlier to not clear the inputs, in case we decide to not delete the message and want to continue editing instead
      // Returning early also keeps the edited message around so it retains the reference and can be deleted when the user confirms.
      return;
    }
    else {
      await conversationMessageStore.editMessage(inputToSend);
    }
  }
  else {
    const messageAttachments
      = filteredUploads.value.length > 0
        ? uploadToAttachment(filteredUploads.value)
        : undefined;

    await conversationMessageStore.createMessage(
      props.conversationState.conversationListResult.id,
      inputToSend,
      undefined,
      messageAttachments,
    );
  }
  clearInputs();
  isSendingMessage.value = false;
}

function toggleEmojiPicker() {
  emojiPickerOpen.value = !emojiPickerOpen.value;
}

async function onEmojiClicked(emoji: string) {
  if (!quill) {
    return;
  }

  const range = quill.getSelection();
  const index = range?.index ?? 0;

  quill.insertText(index, emoji);
  await nextTick();
  quill.setSelection(index + emoji.length, 0);
  quill.focus();

  emojiPickerOpen.value = false;
}

function clearInputs() {
  editingPlainMarkdown = false;
  originalMarkdown = undefined;
  quill?.setContents([{ insert: '' }]);
  input.value = '';
  editingMessage.value = undefined;
  currentEditingIndex.value = null;
  previousMessageContent.value = null;
  conversationState.value.highlightedMessage = undefined;
  attachmentStore.clearUploadsForConversation(
    props.conversationState.conversationListResult.id,
  );
}

function closeEmojiPicker() {
  emojiPickerOpen.value = false;
}
</script>

<template>
  <div class="flex flex-col px-2 pb-2">
    <NoraEmojiPicker
      v-if="emojiPickerOpen && emojiPickerToggle"
      :toggle-ref="emojiPickerToggle"
      @emoji-clicked="(emoji: string) => onEmojiClicked(emoji)"
      @close="closeEmojiPicker"
    />
    <div
      v-if="
        configStore.appConfig?.chatUi.messageTemplates.length
          && filteredUploads.length < 1
      "
      class="mb-1"
    >
      <div
        class="overflow-x-auto whitespace-nowrap"
        :style="{ opacity: input.length < 1 ? '1' : '0.5' }"
      >
        <GrammarQuaternaryButton
          v-for="messageTemplate of configStore.appConfig.chatUi
            .messageTemplates"
          :key="messageTemplate.split(' ').join('_')"
          :label="messageTemplate"
          :disabled="input.length > 1"
          @click.exact="sendTemplateMessage(messageTemplate)"
        />
      </div>
    </div>

    <MessageQuoteInput
      v-if="quoteMessage && quoteMessageInThisConversation"
      :message-state="quoteMessage"
      @close="clearInputs"
    />

    <MessageEditInput v-if="isEditingThisConversation" />

    <div
      class="focus-within:border-light-label-secondary dark:focus-within:border-dark-label-secondary flex flex-col mb-1 mt-1 rounded-sm border"
      :class="[textSecondary, bgSecondary, borderSeparatorNoTransparency]"
    >
      <MessageUploads
        v-if="filteredUploads.length > 0 && !isEditingThisConversation"
        :uploads="filteredUploads"
      />
      <div ref="editor" />
    </div>

    <div class="flex w-full justify-between mb-1">
      <div>
        <GrammarQuaternaryButton
          v-if="canUploadAttachments"
          :disabled="isEditingThisConversation"
          :icon-template="tpgIconAttachFile"
          @click.prevent.stop="showFileUpload()"
        />

        <GrammarQuaternaryButton
          ref="emojiPickerToggle"
          :icon-template="tpgIconAddReaction"
          @click.prevent.stop="toggleEmojiPicker"
        />
      </div>

      <GrammarQuaternaryButton
        :disabled="!canSendMessage"
        :icon-template="tpgIconSend"
        @click.prevent.stop="sendMessage"
      />
    </div>
    <input
      ref="fileInput"
      class="hidden"
      :accept="
        configStore.appConfig?.fileApi.allowedMIMETypes?.join(',') ?? '*/*'
      "
      type="file"
      multiple
      @change="handleFileUpload"
    >
  </div>
</template>

<style scoped>
:deep(.ql-editor) {
  max-height: 200px;
}

.ql-snow .ql-tooltip {
  z-index: 2;
}
</style>
